import { useCallback, useEffect, useState } from "react";
import SidebarItem from "./SidebarItem";
import BuscadorCodigo, { useAtajoBuscador } from "./BuscadorCodigo";
import {
  IconProducts,
  IconCategories,
  IconBrands,
  IconCustomers,
  IconShipping,
  IconOrders,
  IconConfig,
} from "../../icons/Icons";
import { hasPerm } from "../../utils/hasPerm";
import "./Sidebar.css";

const iniciales = (nombre, apellido) => {
  const a = String(nombre || "").trim()[0] || "";
  const b = String(apellido || "").trim()[0] || "";
  const r = `${a}${b}`.toUpperCase();
  return r || "U";
};

const Chevron = ({ hacia }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {hacia === "derecha" ? <path d="M9 18l6-6-6-6" /> : <path d="M15 18l-6-6 6-6" />}
  </svg>
);

const Hamburguesa = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const IconSalir = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

function Sidebar({ collapsed, setCollapsed }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario"));
    } catch {
      return null;
    }
  });

  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "usuario") return;
      try {
        setUser(JSON.parse(e.newValue));
      } catch {
        setUser(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const aplicar = () => {
      setIsMobile(mq.matches);
      if (mq.matches) setMobileOpen(false);
    };

    aplicar();
    mq.addEventListener?.("change", aplicar);
    return () => mq.removeEventListener?.("change", aplicar);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileOpen]);

  const salir = () => {
    // Se borra todo lo de la sesion. Antes se quitaba "token", una clave
    // que nunca se usa, y quedaban en el navegador el JWT real
    // (usuario_token) y los permisos.
    [
      "usuario",
      "usuario_id",
      "usuario_token",
      "permisos",
      "newSession",
    ].forEach((k) => localStorage.removeItem(k));

    window.location.href = "/administrator_jx677/login";
  };

  // El buscador por codigo de barras. Vive en el sidebar y no en la pantalla
  // de productos porque con la pistola en la mano no se quiere navegar
  // primero: se apunta y se dispara desde donde uno este.
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);

  const abrirBuscador = useCallback(() => setBuscadorAbierto(true), []);
  useAtajoBuscador(abrirBuscador);

  // En movil el panel se abre completo: colapsado solo aplica en escritorio.
  const min = isMobile ? false : collapsed;
  const alNavegar = isMobile ? () => setMobileOpen(false) : undefined;

  const clases = [
    "sb",
    min ? "sb--min" : "",
    isMobile ? "sb--movil" : "",
    isMobile && mobileOpen ? "sb--movil-abierto" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const gestionar = [
    hasPerm("View orders") && {
      icon: IconOrders,
      label: "Sales Orders",
      to: "/administrator_jx677/orders",
    },
    hasPerm("View product") && {
      icon: IconProducts,
      label: "Products",
      to: "/administrator_jx677/products",
    },
    hasPerm("View categories") && {
      icon: IconCategories,
      label: "Categories",
      to: "/administrator_jx677/Categories",
    },
    hasPerm("View brand") && {
      icon: IconBrands,
      label: "Brands",
      to: "/administrator_jx677/brands",
    },
    hasPerm("View customer") && {
      icon: IconCustomers,
      label: "Customers",
      to: "/administrator_jx677/customers",
    },
    hasPerm("View shipping methods") && {
      icon: IconShipping,
      label: "Shipping Methods",
      to: "/administrator_jx677/shipping-methods",
    },
  ].filter(Boolean);

  const sistema = [
    hasPerm("View user") && {
      label: "Users",
      to: "/administrator_jx677/config/users",
    },
    hasPerm("View rol") && {
      label: "Roles",
      to: "/administrator_jx677/config/roles",
    },
    hasPerm("View permissions") && {
      label: "Permissions",
      to: "/administrator_jx677/config/Permissions",
    },
  ].filter(Boolean);

  return (
    <>
      {isMobile && !mobileOpen && (
        <button
          type="button"
          className="sb-fab"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          {Hamburguesa}
        </button>
      )}

      {isMobile && mobileOpen && (
        <div
          className="sb-velo"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className={clases} role="navigation" aria-label="Admin sidebar">
        <div className="sb-header">
          <img src="/image/ADC-Logo.png" alt="" className="sb-logo" />

          {!min && (
            <div className="sb-marca">
              <span className="sb-marca-nombre">Aircraft Service &amp; Parts</span>
              <span className="sb-marca-sub">Admin</span>
            </div>
          )}

          <button
            type="button"
            className="sb-toggle"
            onClick={() => {
              if (isMobile) {
                setMobileOpen(false);
                return;
              }
              setCollapsed(!collapsed);
            }}
            aria-label={
              isMobile
                ? "Close menu"
                : collapsed
                ? "Expand sidebar"
                : "Collapse sidebar"
            }
          >
            <Chevron hacia={!isMobile && collapsed ? "derecha" : "izquierda"} />
          </button>
        </div>

        <div className="sb-menu">
          {/* Solo si puede ver productos: lo que abre al escanear es la ficha
              de una pieza. */}
          {hasPerm("View product") && (
            <button
              type="button"
              className="sb-escanear"
              onClick={() => {
                abrirBuscador();
                if (isMobile) setMobileOpen(false);
              }}
              title="Scan a barcode  (Ctrl+K)"
            >
              <span className="sb-escanear-icono" aria-hidden="true">
                {IconoEscanear}
              </span>
              <span className="sb-escanear-texto">Scan barcode</span>
              <span className="sb-escanear-atajo">Ctrl K</span>
            </button>
          )}

          {gestionar.length > 0 && (
            <div className="sb-grupo">
              <span className="sb-grupo-titulo">Manage</span>

              {gestionar.map((it) => (
                <SidebarItem
                  key={it.to}
                  icon={it.icon}
                  label={it.label}
                  to={it.to}
                  collapsed={min}
                  onNavigate={alNavegar}
                />
              ))}
            </div>
          )}

          {sistema.length > 0 && (
            <div className="sb-grupo">
              <span className="sb-grupo-titulo">System</span>

              <SidebarItem
                icon={IconConfig}
                label="Configuration"
                collapsed={min}
                onNavigate={alNavegar}
                children={sistema}
              />
            </div>
          )}
        </div>

        <div className="sb-pie">
          <span className="sb-avatar" aria-hidden="true">
            {iniciales(user?.nombre, user?.apellido)}
          </span>

          {!min && (
            <div className="sb-usuario">
              <span className="sb-usuario-nombre">
                {[user?.nombre, user?.apellido].filter(Boolean).join(" ") ||
                  "User"}
              </span>
              <span className="sb-usuario-rol">{user?.correo || ""}</span>
            </div>
          )}

          <button
            type="button"
            className="sb-salir"
            onClick={salir}
            title="Log out"
          >
            {IconSalir}
            {!min && <span>Log out</span>}
          </button>
        </div>
      </div>

      <BuscadorCodigo
        abierto={buscadorAbierto}
        onCerrar={() => setBuscadorAbierto(false)}
      />
    </>
  );
}

const IconoEscanear = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 6v12M7 6v12M10.5 6v12M14 6v12M17.5 6v12M20 6v12"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

export default Sidebar;
